import { useState, type FormEvent, type ReactNode } from 'react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire, MarqueObligatoire, MessageErreur } from '../ui/Primitives';
import { messageRefus } from '../../lib/useFormateurs';
import {
  useInviterUtilisateur,
  type ResultatInvitation,
  type VueUtilisateurs,
} from '../../lib/useUtilisateurs';

const CLASSE_CHAMP = 'w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-codialis-navy';

function Champ({ libelle, children }: { libelle: string; children: ReactNode }): JSX.Element {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
        {libelle}
        <MarqueObligatoire />
      </span>
      {children}
    </label>
  );
}

interface Formulaire {
  prenom: string;
  nom: string;
  email: string;
  profil: string;
  sousProfil: string | null;
  statutFormateur: string;
  sites: string[];
}

function vierge(vue: VueUtilisateurs): Formulaire {
  return {
    prenom: '',
    nom: '',
    email: '',
    profil: '',
    sousProfil: null,
    statutFormateur: vue.statutsFormateur[0]?.statut ?? '',
    // Une seule antenne ouverte : elle est cochée d'office.
    sites: vue.antennes.length === 1 ? vue.antennes.map((antenne) => antenne.code) : [],
  };
}

/**
 * Invitation d'un nouvel utilisateur par la direction (TF-500) : identité,
 * profil, antennes. Le compte naît aussitôt ; la personne reçoit un lien pour
 * choisir son mot de passe. Les règles (sous-profil, une seule antenne pour
 * l'assistant(e), adresse déjà prise) sont celles de l'API, qui dit ce qui
 * bloque.
 */
export function ModaleInvitation({
  vue,
  ouverte,
  onFermer,
  onInvite,
}: {
  vue: VueUtilisateurs;
  ouverte: boolean;
  onFermer: () => void;
  onInvite: (resultat: ResultatInvitation) => void;
}): JSX.Element {
  const inviter = useInviterUtilisateur();
  const [formulaire, setFormulaire] = useState<Formulaire>(() => vierge(vue));
  const [erreur, setErreur] = useState<string | null>(null);

  const profil = vue.profils.find((un) => un.profil === formulaire.profil) ?? null;
  const uneSeuleAntenne = formulaire.sousProfil !== null;

  const changer = <C extends keyof Formulaire>(cle: C, valeur: Formulaire[C]) =>
    setFormulaire((avant) => ({ ...avant, [cle]: valeur }));

  const choisirProfil = (valeur: string) =>
    setFormulaire((avant) => ({ ...avant, profil: valeur, sousProfil: null }));

  const basculerSousProfil = (sousProfil: string, coche: boolean) =>
    setFormulaire((avant) => ({
      ...avant,
      sousProfil: coche ? sousProfil : null,
      // L'assistant(e) n'a qu'une antenne : on garde la première cochée.
      sites: coche ? avant.sites.slice(0, 1) : avant.sites,
    }));

  const basculerAntenne = (code: string) =>
    setFormulaire((avant) => {
      if (uneSeuleAntenne) return { ...avant, sites: [code] };
      const sites = avant.sites.includes(code)
        ? avant.sites.filter((site) => site !== code)
        : [...avant.sites, code];
      return { ...avant, sites };
    });

  const fermer = () => {
    setFormulaire(vierge(vue));
    setErreur(null);
    onFermer();
  };

  const envoyer = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    try {
      const resultat = await inviter.mutateAsync({
        prenom: formulaire.prenom,
        nom: formulaire.nom,
        email: formulaire.email,
        profil: formulaire.profil,
        sousProfil: formulaire.sousProfil,
        sites: formulaire.sites,
        statutFormateur: formulaire.profil === 'FORMATEUR' ? formulaire.statutFormateur : null,
      });
      setFormulaire(vierge(vue));
      onInvite(resultat);
    } catch (cause) {
      setErreur(messageRefus(cause, 'L’invitation n’a pas pu être envoyée.'));
    }
  };

  return (
    <Modale
      titre="Inviter un utilisateur"
      sousTitre="Le compte est créé aussitôt ; la personne choisit son mot de passe par le lien reçu."
      ouverte={ouverte}
      onFermer={fermer}
    >
      <form onSubmit={(evenement) => void envoyer(evenement)} className="space-y-5 p-4 sm:p-6" noValidate>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Champ libelle="Prénom">
            <input
              value={formulaire.prenom}
              onChange={(evenement) => changer('prenom', evenement.target.value)}
              autoComplete="off"
              required
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ libelle="Nom">
            <input
              value={formulaire.nom}
              onChange={(evenement) => changer('nom', evenement.target.value)}
              autoComplete="off"
              required
              className={CLASSE_CHAMP}
            />
          </Champ>
        </div>

        <Champ libelle="Adresse e-mail">
          <input
            type="email"
            value={formulaire.email}
            onChange={(evenement) => changer('email', evenement.target.value)}
            autoComplete="off"
            required
            className={CLASSE_CHAMP}
          />
        </Champ>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Champ libelle="Profil">
            <select
              value={formulaire.profil}
              onChange={(evenement) => choisirProfil(evenement.target.value)}
              required
              className={CLASSE_CHAMP}
            >
              <option value="">Choisir un profil</option>
              {vue.profils.map((un) => (
                <option key={un.profil} value={un.profil}>
                  {un.libelle}
                </option>
              ))}
            </select>
          </Champ>
          {formulaire.profil === 'FORMATEUR' && (
            <Champ libelle="Statut">
              <select
                value={formulaire.statutFormateur}
                onChange={(evenement) => changer('statutFormateur', evenement.target.value)}
                className={CLASSE_CHAMP}
              >
                {vue.statutsFormateur.map((statut) => (
                  <option key={statut.statut} value={statut.statut}>
                    {statut.libelle}
                  </option>
                ))}
              </select>
            </Champ>
          )}
        </div>

        {profil?.sousProfils.map((sousProfil) => (
          <label key={sousProfil.sousProfil} className="flex items-center gap-3 text-sm text-codialis-navy">
            <input
              type="checkbox"
              checked={formulaire.sousProfil === sousProfil.sousProfil}
              onChange={(evenement) => basculerSousProfil(sousProfil.sousProfil, evenement.target.checked)}
              className="h-4 w-4 rounded text-codialis-mint focus:ring-codialis-mint"
            />
            {sousProfil.libelle} (une seule antenne)
          </label>
        ))}

        <fieldset className="min-w-0">
          <legend className="mb-2 text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            {uneSeuleAntenne ? 'Antenne' : 'Antennes'}
            <MarqueObligatoire />
          </legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {vue.antennes.map((antenne) => (
              <label
                key={antenne.code}
                className="flex cursor-pointer items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 transition hover:bg-gray-100/70"
              >
                <input
                  type={uneSeuleAntenne ? 'radio' : 'checkbox'}
                  name="antennes-invitation"
                  checked={formulaire.sites.includes(antenne.code)}
                  onChange={() => basculerAntenne(antenne.code)}
                  className="h-4 w-4 rounded text-codialis-mint focus:ring-codialis-mint"
                />
                <span className="min-w-0 flex-1 text-sm font-medium text-codialis-navy">
                  {antenne.nom} ({antenne.code})
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <p className="rounded-xl bg-codialis-mint/10 p-4 text-xs leading-relaxed text-codialis-navy">
          {vue.courrielExpedie
            ? `Un e-mail part à cette adresse avec un lien pour choisir son mot de passe, valable ${vue.validiteInvitationJours} jours.`
            : `Les e-mails ne partent pas encore depuis la plateforme : le lien, valable ${vue.validiteInvitationJours} jours, vous sera affiché une fois pour que vous le transmettiez.`}
        </p>

        {erreur && <MessageErreur message={erreur} />}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <BoutonSecondaire onClick={fermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={inviter.isPending}>
            {inviter.isPending ? 'Envoi…' : 'Envoyer l’invitation'}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}
